import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchPlaceType,
  deletePlaceType,
  addPlaceTypeCriteria,
  deletePlaceTypeCriteria,
  addPlaceTypeCriteriaValue,
  deletePlaceTypeCriteriaValue,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function PlaceTypeDetailPage() {
  const { businessDomainId, placeTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Criteria state ──
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({ classificationCriteriaCd: "", classificationCriteriaName: "" });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // ── Criteria Value state ──
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try { setData(await fetchPlaceType(placeTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeTypeId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Delete place type ──
  const handleDelete = async () => {
    try {
      await deletePlaceType(placeTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/place-types`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── Criteria CRUD ──
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addPlaceTypeCriteria(placeTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success"));
      setCriteriaDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCriteriaDelete = async () => {
    try {
      await deletePlaceTypeCriteria(placeTypeId, criteriaDeleteTarget);
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addPlaceTypeCriteriaValue(placeTypeId, {
        classificationCriteria: selectedCriteria,
        classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd,
        classificationCriteriaValueName: cvForm.classificationCriteriaValueName,
        createdValueAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      setCvDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCvDelete = async () => {
    try {
      await deletePlaceTypeCriteriaValue(placeTypeId, cvDeleteTarget);
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  if (!data) return null;

  const criterias = data.classificationCriterias || [];
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter(
        (v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName
      )
    : [];

  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("placeCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("placeCriteria.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setCriteriaDeleteTarget({ classificationCriteriaCd: p.row.classificationCriteriaCd, classificationCriteriaName: p.row.classificationCriteriaName });
          setCriteriaConfirmOpen(true);
        }}><DeleteIcon fontSize="small" /></IconButton>
      ),
    },
  ];

  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCvDeleteTarget(p.row); setCvConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("placeType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/place-types/${placeTypeId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("placeType.name")} value={data.placeTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("placeType.description")} value={data.placeTypeDescription} /></Grid>
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("placeCriteria.title")} />
        </Tabs>
      </Box>

      {/* Tab 0: Place Criteria */}
      <TabPanel value={tabIndex} index={0}>
        {selectedCriteria ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small"><ArrowBackIcon /></IconButton>
              <Typography variant="subtitle1">
                {selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" }); setCvDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criteriaValues} columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              key={criteriaValues.map((v) => v.classificationCriteriaValueCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick />
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("placeCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" }); setCriteriaDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              key={criterias.map((c) => c.classificationCriteriaCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <Box sx={{ mt: 3 }}><Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/place-types`)}>{t("common.back")}</Button></Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete} onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete} onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />

      {/* Criteria Add Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("placeCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("placeCriteria.code")} value={criteriaForm.classificationCriteriaCd}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("placeCriteria.name")} value={criteriaForm.classificationCriteriaName}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

      {/* Criteria Value Add Dialog */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")} value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")} value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
